// The site's own count of its readers. `useCountViews` is the reporting end,
// run once at the root; `useSiteStats` is the admin's page reading it back.
import type { StatsRange, StatsView, ToolUsageView } from "@bibleunleashed/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { api } from "./api";

export function useSiteStats(days: StatsRange) {
  return useQuery({ queryKey: ["stats", days], queryFn: () => api<StatsView>(`/api/stats?days=${days}`), staleTime: 60_000 });
}

export function useToolUsage(days: StatsRange) {
  return useQuery({ queryKey: ["stats", "tools", days], queryFn: () => api<ToolUsageView>(`/api/stats/tools?days=${days}`), staleTime: 60_000 });
}

/**
 * Reports each page the reader opens — its path, and on the first one only
 * where they came from. A path that changes again within the moment (a
 * redirect, /read/… to /commentary/…) is not a page anyone saw, so the
 * report waits a beat and is dropped if the path has moved on. The server
 * decides what to keep: never the admin's own reading, never a crawler.
 */
export function useCountViews(): void {
  const [path] = useLocation();
  // Spent by the first report that goes out, whichever page that turns out to be.
  const arrivedFrom = useRef(document.referrer);

  useEffect(() => {
    const t = setTimeout(() => {
      const referrer = arrivedFrom.current;
      arrivedFrom.current = "";
      void fetch("/api/stats/view", {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, ...(referrer ? { referrer } : {}) }),
      }).catch(() => undefined);
    }, 400);
    return () => clearTimeout(t);
  }, [path]);
}
